import { api as bffHttpClient } from '@/utils/Api';
import { to } from '@/utils/to';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

// Cache object to store memoized allIds by clientId
const idsCache: Record<string, string[]> = {};

export interface TreeNode {
  id: string;
  label: string;
  path: string[];
  secondaryText: string;
  leafNodeCount: number;
  children: TreeNode[];
}

export interface ItemsMapItem {
  label: string;
  secondaryText: string;
  path: string[];
}

export interface NavigationTreeResponse {
  data: {
    tree: TreeNode | null;
    itemsMap: Record<string, ItemsMapItem>;
    itemsOrder: string[];
    totalItemsCount: number;
  };
}

interface Params {
  clientId?: string;
  searchField?: string;
}

async function getAdvancedNavigationTree(params: Params) {
  const { clientId, searchField } = params;

  const [data, error] = await to(
    bffHttpClient.get<NavigationTreeResponse>(
      `/navigation/projects/${clientId}/tree`,
      {
        params: {
          ...(searchField ? { searchField } : {}),
        },
      }
    )
  );

  if (error) {
    return null;
  }

  return data.data;
}

const queryKey = 'advancedNavigationTree';

type UseAdvancedNavigationTreeProps = Params &
  Omit<
    UseQueryOptions<NavigationTreeResponse | null>,
    'placeholderData' | 'queryFn' | 'queryKey' | 'select'
  >;

function useAdvancedNavigationTree(props: UseAdvancedNavigationTreeProps) {
  const { clientId, ...otherProps } = props;
  const queryResult = useQuery({
    queryFn: () => getAdvancedNavigationTree({ clientId }),
    staleTime: Infinity,
    queryKey: [queryKey, clientId],
    select(response) {
      if (!response) return null;
      const { data } = response;
      const { tree, ...otherData } = data;
      if (!tree) return null;

      // Use cached values if available for this client
      let allItems: string[] = [];

      // Use tree.id as the cache key instead of clientId parameter
      const cacheKey = tree.id;

      if (cacheKey) {
        // Check cache first
        if (idsCache[cacheKey]) {
          allItems = idsCache[cacheKey];
        } else {
          const allIds = new Set<string>();

          function collectIds(items: TreeNode[]) {
            if (!items || !items.length) return;

            for (const item of items) {
              if (item.id) {
                allIds.add(item.id);
              }
              if (item.children && item.children.length > 0) {
                collectIds(item.children);
              }
            }
          }

          collectIds(tree.children);
          allItems = Array.from(allIds);

          // Cache the result using tree.id
          if (cacheKey) {
            idsCache[cacheKey] = allItems;
          }
        }
      }

      // Build parent-to-children map for efficient selection logic
      const parentToChildrenMap: Record<string, string[]> = {};
      function buildMaps(node: TreeNode) {
        if (node.children && node.children.length > 0) {
          parentToChildrenMap[node.id] = node.children.map(child => child.id);
          node.children.forEach(buildMaps);
        }
      }
      buildMaps(tree);

      return {
        allItems,
        parentToChildrenMap,
        tree: {
          ...tree,
          label: 'All Buildings',
        },
        ...otherData,
      };
    },
    ...otherProps,
  });

  return queryResult;
}

export { queryKey, useAdvancedNavigationTree, getAdvancedNavigationTree };
